import React, { useState, useEffect } from 'react';
import { Music2, TrendingUp, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// Real trending music — Apple Music charts (public iTunes RSS, CORS-enabled,
// no auth). The India storefront best matches our Bollywood/Punjabi/Desi
// audience. Each track also gets a Spotify deep-link so users can open it on
// either platform.
const APPLE_STOREFRONT = 'in';
const APPLE_CHART_URL = `https://music.apple.com/${APPLE_STOREFRONT}/browse`;
const SPOTIFY_BROWSE_URL = 'https://open.spotify.com/genre/0JQ5DAt0tbjZptfcdMSKl3'; // Spotify Browse / Charts

const feedUrl = (limit) =>
  `https://itunes.apple.com/${APPLE_STOREFRONT}/rss/topsongs/limit=${limit}/json`;

const spotifySearchUrl = (artist, title) =>
  `https://open.spotify.com/search/${encodeURIComponent(`${artist} ${title}`)}`;

function mapEntry(entry, index) {
  const images = entry['im:image'] || [];
  const rawCover = images.length ? images[images.length - 1].label : '';
  // Bump Apple's artwork from the RSS thumbnail size up to a crisp 300px.
  const cover = rawCover.replace(/\/\d+x\d+bb\.(png|jpg|jpeg)/i, '/300x300bb.$1');

  const title = entry['im:name']?.label || 'Untitled';
  const artist = entry['im:artist']?.label || 'Unknown Artist';
  const album = entry['im:collection']?.['im:name']?.label || '';
  const genre = entry.category?.attributes?.label || 'Music';
  const releaseDate = entry['im:releaseDate']?.attributes?.label || '';

  const links = Array.isArray(entry.link) ? entry.link : entry.link ? [entry.link] : [];
  const appleUrl =
    (links.find((l) => l.attributes?.rel === 'alternate') || links[0])?.attributes?.href || '';

  return {
    id: entry.id?.attributes?.['im:id'] || `${index}`,
    title,
    artist,
    album,
    genre,
    releaseDate,
    cover,
    appleUrl,
    spotifyUrl: spotifySearchUrl(artist, title),
  };
}

export const TrendingMusicReleases = ({ limit = 6 }) => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(feedUrl(Math.max(limit, 10)));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const entries = data?.feed?.entry || [];
        const mapped = entries.map(mapEntry).filter((t) => t.cover).slice(0, limit);
        if (!active) return;
        if (mapped.length === 0) {
          setFailed(true);
        } else {
          setReleases(mapped);
        }
      } catch (e) {
        if (active) setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [limit]);

  // Hide the whole section if the live charts can't be loaded — never show
  // stale or broken data on a live site.
  if (failed) return null;

  const open = (url) => url && window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <section className="py-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <Music2 className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold">Trending Music</h2>
              <p className="text-sm text-muted-foreground">Live charts from Apple Music &amp; Spotify</p>
            </div>
          </div>
          <Button variant="ghost" onClick={() => open(APPLE_CHART_URL)}>
            View Charts
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading
            ? Array.from({ length: limit }).map((_, i) => (
                <Card key={i} className="overflow-hidden border-border/50">
                  <CardContent className="p-4">
                    <div className="flex gap-4 animate-pulse">
                      <div className="w-20 h-20 rounded-lg bg-muted flex-shrink-0" />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                        <div className="h-3 bg-muted rounded w-2/3" />
                        <div className="h-6 bg-muted rounded w-full mt-2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            : releases.map((release, index) => (
                <Card
                  key={release.id}
                  className="group overflow-hidden border-border/50 hover-lift cursor-pointer transition-all duration-300"
                  onClick={() => open(release.appleUrl)}
                >
                  <CardContent className="p-0">
                    <div className="flex gap-4 p-4">
                      {/* Album cover */}
                      <div className="relative flex-shrink-0">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted">
                          <img
                            src={release.cover}
                            alt={`${release.title} by ${release.artist}`}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        </div>
                        {index < 3 && (
                          <div className="absolute -top-2 -left-2">
                            <Badge className="bg-green-500 text-white font-bold px-2 py-0.5 text-xs">
                              #{index + 1}
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Track info */}
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold truncate group-hover:text-green-500 transition-colors">
                            {release.title}
                          </h3>
                          {index < 3 && (
                            <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{release.artist}</p>

                        <div className="flex items-center gap-2 pt-0.5">
                          <Badge variant="secondary" className="text-xs">
                            {release.genre}
                          </Badge>
                          {release.releaseDate && (
                            <span className="text-xs text-muted-foreground truncate">
                              {release.releaseDate}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              open(release.appleUrl);
                            }}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Apple Music
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-green-500 hover:text-green-400 hover:bg-green-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              open(release.spotifyUrl);
                            }}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Spotify
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
        </div>

        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            🎧 Live from the Apple Music charts • open any track on{' '}
            <button
              className="underline hover:text-foreground"
              onClick={() => open(SPOTIFY_BROWSE_URL)}
            >
              Spotify
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

export default TrendingMusicReleases;
